import { appConfig, buildInfo } from "../config/appConfig";
import { getAccessToken, getCurrentAuthState } from "./auth";

export type Practice = {
  id: string;
  name: string;
  address: string;
  isPrimary: boolean;
  status: "active" | "pending" | "not_approved";
};

export type PracticeOption = {
  id: string;
  name: string;
  label: string;
  address: string;
};

export type PracticeAddress = {
  id?: string;
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  postalCode: string;
};

export type AccountProfile = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  deaNumber: string;
  licenseNumber: string | null;
  licenseState: string | null;
  hasSignature: boolean;
  signaturePreviewUrl: string | null;
  practices: Practice[];
};

export type AccountUpdate = Partial<Pick<AccountProfile, "firstName" | "lastName" | "phone" | "deaNumber">>;
export type PracticeCreate =
  | {
      mode: "existing";
      practiceId: string;
    }
  | {
      mode: "manual";
      name: string;
      address: PracticeAddress;
    };
export type AccountDeleteRequest = {
  confirmation: "DELETE";
};

export type AccessRequestInput = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  practiceName: string;
  practiceId?: string;
  practiceMode?: "existing" | "manual";
  licenseNumber: string;
  licenseState: string;
  turnstileToken: string;
  termsAccepted: boolean;
};

export type AccessRequestResult = {
  ok: boolean;
  status: number;
  code?: string;
  message: string;
  duplicateBy?: "email" | "license" | "unknown";
};

export type FeedbackInput = {
  category: "bug" | "idea" | "question" | "other";
  message: string;
  pageUrl: string;
};

export type FeedbackResult = {
  ok: true;
  status: "sent" | "received_not_configured";
  message: string;
  ticketId?: string | null;
  ticketNumber?: string | null;
};

export type RegistrationPrefill = {
  approved: boolean;
  profile: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    deaNumber: string;
    licenseNumber: string | null;
    licenseState: string | null;
  } | null;
  practice: {
    id: string | null;
    name: string;
  } | null;
};

export type RegistrationCompleteInput = {
  firstName: string;
  lastName: string;
  phone: string;
  deaNumber?: string;
  signatureBase64?: string;
};

export type RegistrationCompleteResult = {
  ok: true;
  account: AccountProfile;
};

export type RegistrationLinkRequestResult = {
  ok: true;
  queued: boolean;
  code: "queued" | "not_eligible";
  message: string;
};

export type ApiHealth = {
  ok: boolean;
  environment: string;
  authenticated: boolean;
};

export type RuntimePortalConfig = {
  environment: string;
  showTestBanner: boolean;
  testBannerText: string | null;
  support: {
    email: string;
    phone: string;
    phoneHref: string;
    technologyEmail: string;
  };
  maintenance: {
    enabled: boolean;
    title: string;
    message: string;
  };
  legal: {
    privacyPolicyPath: string;
    termsOfServicePath: string;
  };
};

export type OrderSummary = {
  orderId: string;
  orderCode?: string | null;
  orderDate: string | null;
  status: string;
  statusDefinition?: string | null;
  sourceType?: "portal" | "historical";
  sourceLabel?: string | null;
  patientName: string;
  patientLastName?: string | null;
  patientDob?: string | null;
  patientSex?: string | null;
  patientSpecies?: string | null;
  patientWeight?: string | null;
  ownerName?: string | null;
  ownerPhone?: string | null;
  ownerEmail?: string | null;
  ownerAddress?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postalCode?: string | null;
    display?: string | null;
  };
  practiceId?: string | null;
  practiceName?: string | null;
  drugName: string;
  expirationDate: string | null;
  refillUsage: string | null;
  pdfAvailable: boolean;
};

export type OrderListResult = {
  orders: OrderSummary[];
  warnings?: string[];
  pagination?: {
    limit: number;
    offset: number;
    returned: number;
    hasPrevious: boolean;
    hasNext: boolean;
  };
};

export type OrderDetail = OrderSummary & {
  practiceId: string | null;
  practice: {
    id: string | null;
    name: string | null;
    address: string | null;
    phone: string | null;
  };
  patient: {
    firstName: string | null;
    lastName: string | null;
    name: string;
    dob: string | null;
    sex: string | null;
    species: string | null;
    weight: string | null;
  };
  owner: {
    firstName: string | null;
    lastName: string | null;
    name: string | null;
    phone: string | null;
    email: string | null;
    address: {
      line1: string | null;
      line2: string | null;
      city: string | null;
      state: string | null;
      postalCode: string | null;
      display: string | null;
    };
  };
  prescription: {
    strength: string | null;
    dosageForm: string | null;
    quantity: string | null;
    refills: number | null;
    prn: boolean;
    expirationDate: string | null;
    directions: string | null;
    notes: string | null;
    communicationMethod: string | null;
    deliveryMechanism: string | null;
    pharmetikaRxNumber: string | null;
  };
  signature: {
    mode: string | null;
    capturedAt: string | null;
  };
  transactionHistory: Array<{
    id: string;
    label: string;
    status: string | null;
    statusDefinition?: string | null;
    occurredAt: string | null;
    detail: string | null;
  }>;
};

export type OrderPdfStatus = {
  available: boolean;
  status: "ready" | "pending" | "queued";
  message?: string;
  generatedAt?: string;
  sizeBytes?: number;
  downloadUrl?: string;
  expiresAt?: string;
  messageId?: string | null;
  templateKey?: string;
};

export type SubmitPrescriptionInput = {
  practiceId: string;
  patientId?: string;
  patient: {
    name?: string;
    firstName?: string;
    lastName?: string;
    dob: string;
    sex: string;
    species: string;
    weight: string;
    weightUnit?: string;
  };
  owner: {
    firstName: string;
    lastName: string;
    phone: string;
    email?: string;
    address: {
      line1: string;
      line2?: string;
      city: string;
      state: string;
      postalCode: string;
    };
  };
  prescription: {
    productId: string;
    strength?: string;
    dosageForm?: string;
    quantity: string;
    refills?: string;
    expirationDate: string;
    directions: string;
    prn: boolean;
    notes?: string;
  };
  communicationPreferences: {
    text: boolean;
    phone: boolean;
    email: boolean;
  };
  selectedPatientBaseline?: Record<string, unknown>;
  patientDemographicDecision?: "update_existing" | "create_new" | "no_change";
  ownerDetailsDecision?: "update_current" | "create_new" | "no_change";
  signatureBase64?: string;
  useSavedSignature?: boolean;
};

export type SubmitPrescriptionResult = {
  ok: true;
  order: OrderSummary;
  pdfStatus: OrderPdfStatus;
  warnings: string[];
};

export type OrderFormPrefill = {
  selectedPractice: {
    id: string;
    name: string;
    address: string;
    isPrimary: boolean;
  };
  patientOptions: Array<{
    patientId: string;
    label: string;
    source: string | null;
    sourceLabel?: string | null;
    sources: string[];
    sourceEvidence?: unknown;
    canUpdateDemographics: boolean;
    demographics: {
      name: string;
      firstName: string | null;
      lastName: string | null;
      dob: string | null;
      sex: string | null;
      sexLabel: string | null;
      species: string | null;
      speciesLabel: string | null;
      weight: string | null;
      weightUnit: string | null;
    };
    owner: {
      firstName: string | null;
      lastName: string | null;
      phone: string | null;
      email: string | null;
      address: {
        line1: string | null;
        line2: string | null;
        city: string | null;
        state: string | null;
        postalCode: string | null;
      };
    };
  }>;
  productOptions: Array<{
    productId: string;
    label: string;
    drugName: string;
    strength: string | null;
    dosageForm: string | null;
    defaultQuantity: string | null;
    quantityUnit: string | null;
    deaSchedule: string | null;
  }>;
  speciesOptions: Array<{
    value: string;
    label: string;
    sourceCode: string;
    sourceLabel: string;
  }>;
  warnings: string[];
};

export type ProductDetail = OrderFormPrefill["productOptions"][number] & {
  quantityRules: {
    defaultQuantity: string | null;
    customQuantityAllowed: boolean;
    customQuantityMustBeBelow: string | null;
    decimalQuantityAllowed: boolean;
    allowedDecimalQuantities: string[];
    decimalQuantityIncrements?: string[];
    decimalQuantityPlaces?: number | null;
    minimumOrderableQuantity: string | null;
  };
  quantityOptions: Array<{
    id: string;
    label: string;
    value: string;
    minimumQuantity: string | null;
    maximumQuantity: string | null;
    isCustomQuantity: boolean;
    unitPrice: string | null;
    additionalFee: string | null;
    unitPercentage: string | null;
  }>;
  pricing: {
    selectedQuantity: string | null;
    selectedPricePlanEntryId: string | null;
    unitPrice: string | null;
    additionalFee: string | null;
    unitPercentage: string | null;
    minimumPrice: string | null;
    minimumPriceApplied: boolean;
    estimatedPrice: string | null;
    message: string | null;
  };
  validation?: {
    quantity: {
      ok: boolean;
      message: string | null;
    };
  };
};

export type PreviousPrescriptionOption = {
  prescriptionId: string;
  label: string;
  sourceLabel: string | null;
  practiceAssociation: string | null;
  productId: string | null;
  reportProductListId: string | null;
  pharmacySystemProductId: string | null;
  ndc: string | null;
  productName: string;
  strength: string | null;
  dosageForm: string | null;
  quantity: string | null;
  refills: string | null;
  directions: string | null;
  expirationDate: string | null;
  writtenDate: string | null;
};

export type PreviousPrescriptionsResult = {
  options: PreviousPrescriptionOption[];
  warnings: string[];
};

const mockAccount: AccountProfile = {
  firstName: "Jill",
  lastName: "Austin",
  email: "demo.prescriber@companionpharmacy.com",
  phone: "(919) 505-8779",
  deaNumber: "FA1234567",
  licenseNumber: "VM-12345",
  licenseState: "NC",
  hasSignature: true,
  signaturePreviewUrl: null,
  practices: [
    {
      id: "practice-haw-river",
      name: "Haw River Animal Hospital",
      address: "40 Ramseur Road Suite 103, Pittsboro, NC 27312",
      isPrimary: true,
      status: "active"
    },
    {
      id: "practice-alexander-park",
      name: "Alexander at the Park Veterinary Hospital",
      address: "111 Example Parkway, Durham, NC 27701",
      isPrimary: false,
      status: "active"
    }
  ]
};

function shouldUseMockApi(): boolean {
  return appConfig.useMockApi;
}

const RETRYABLE_STATUS_CODES = new Set([408, 429, 502, 503, 504]);
const TELEMETRY_ENDPOINT = "/api/telemetry/client-error";
const TELEMETRY_STATUS_CODES = new Set([400, 401, 403, 404, 408, 409, 422, 429, 500, 502, 503, 504]);
const telemetryDedupeWindowMs = 60_000;
const lastTelemetryAt = new Map<string, number>();

export class PortalApiError extends Error {
  status?: number;
  payload?: unknown;
  validation?: unknown;
  responseText?: string;
  code?: string;
  isEdgeBlocked: boolean;
  isRetryable: boolean;

  constructor(message: string, options: {
    status?: number;
    payload?: unknown;
    validation?: unknown;
    responseText?: string;
    code?: string;
    isEdgeBlocked?: boolean;
    isRetryable?: boolean;
  }) {
    super(message);
    this.name = "PortalApiError";
    this.status = options.status;
    this.payload = options.payload;
    this.validation = options.validation;
    this.responseText = options.responseText;
    this.code = options.code;
    this.isEdgeBlocked = options.isEdgeBlocked === true;
    this.isRetryable = options.isRetryable === true;
  }
}

function isSafeRequestMethod(method: string): boolean {
  return ["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

function isCloudFrontBlockedResponse(status: number, responseText: string): boolean {
  return (
    status === 403 &&
    /Generated by cloudfront|The request could not be satisfied|Request blocked/i.test(responseText)
  );
}

function retryDelayMs(attempt: number): number {
  return 250 * 2 ** attempt;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function isAbortError(cause: unknown): boolean {
  return (
    cause instanceof DOMException && cause.name === "AbortError"
  ) || (
    cause instanceof Error && cause.name === "AbortError"
  );
}

function routeTemplateFor(path: string): string {
  const [pathname] = path.split("?");
  return pathname
    .split("/")
    .map((part) => {
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(part)) {
        return "{id}";
      }
      if (/^[A-Z0-9]{8,12}$/.test(part)) {
        return "{code}";
      }
      return part;
    })
    .join("/");
}

function safePageUrl(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

function telemetryKey(path: string, method: string, status: number | undefined, code: string | undefined): string {
  return [routeTemplateFor(path), method, status || "network", code || ""].join("|");
}

function shouldEmitTelemetry(status: number | undefined, code: string | undefined, isEdgeBlocked: boolean): boolean {
  return isEdgeBlocked ||
    code === "auth_link_expired" ||
    code === "auth_link_verification_failed" ||
    code === "network_error" ||
    code === "frontend_runtime_error" ||
    code === "frontend_unhandled_rejection" ||
    (typeof status === "number" && TELEMETRY_STATUS_CODES.has(status));
}

function apiUrlFor(path: string): string {
  return appConfig.portalApiBaseUrl ? `${appConfig.portalApiBaseUrl}${path}` : path;
}

async function emitClientErrorTelemetry(input: {
  path: string;
  method: string;
  status?: number;
  code?: string;
  isRetryable: boolean;
  isEdgeBlocked: boolean;
  responseText?: string;
  requestId?: string | null;
  reachability?: "api_response" | "edge_blocked" | "network_error" | "unknown";
}): Promise<void> {
  if (input.path === TELEMETRY_ENDPOINT) return;
  if (!shouldEmitTelemetry(input.status, input.code, input.isEdgeBlocked)) return;

  const key = telemetryKey(input.path, input.method, input.status, input.code);
  const now = Date.now();
  const previous = lastTelemetryAt.get(key) || 0;
  if (now - previous < telemetryDedupeWindowMs) return;
  lastTelemetryAt.set(key, now);

  try {
    const [accessToken, authState] = await Promise.all([
      getAccessToken().catch(() => null),
      getCurrentAuthState().catch(() => null)
    ]);
    const headers = new Headers({ "Content-Type": "application/json" });
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);

    void fetch(apiUrlFor(TELEMETRY_ENDPOINT), {
      method: "POST",
      headers,
      body: JSON.stringify({
        routeTemplate: routeTemplateFor(input.path),
        method: input.method,
        status: input.status ?? null,
        errorCode: input.code || (input.isEdgeBlocked ? "edge_request_blocked" : "request_failed"),
        retryable: input.isRetryable,
        edgeBlocked: input.isEdgeBlocked,
        reachability: input.reachability ||
          (input.code === "network_error" ? "network_error" : input.isEdgeBlocked ? "edge_blocked" : "api_response"),
        requestId: input.requestId || null,
        build: buildInfo,
        pageUrl: safePageUrl(),
        user: authState?.user
          ? {
              id: authState.user.id,
              email: authState.user.email
            }
          : null
      }),
      keepalive: true
    }).catch(() => undefined);
  } catch {
    // Telemetry must never block or alter the user-facing workflow.
  }
}

export function emitPortalTelemetry(input: {
  routeTemplate: string;
  method?: string;
  status?: number;
  errorCode: string;
  retryable?: boolean;
  edgeBlocked?: boolean;
  reachability?: "api_response" | "edge_blocked" | "network_error" | "unknown";
  requestId?: string | null;
}): void {
  const path = input.routeTemplate.startsWith("/api/") ? input.routeTemplate : "/api/client-runtime";
  void emitClientErrorTelemetry({
    path,
    method: input.method || "POST",
    status: input.status,
    code: input.errorCode,
    isRetryable: input.retryable === true,
    isEdgeBlocked: input.edgeBlocked === true,
    requestId: input.requestId || null,
    reachability: input.reachability || "unknown"
  });
}

async function request<T>(path: string, init?: RequestInit, authContext?: { accessToken: string | null }): Promise<T> {
  if (shouldUseMockApi()) {
    throw new Error(`No mock handler for ${path}`);
  }

  const accessToken = authContext ? authContext.accessToken : await getAccessToken();
  const headers = new Headers(init?.headers);
  headers.set("Content-Type", "application/json");
  if (accessToken) {
    headers.set("Authorization", `Bearer ${accessToken}`);
  }

  const method = String(init?.method || "GET").toUpperCase();
  const canRetry = isSafeRequestMethod(method);
  const maxAttempts = canRetry ? 3 : 1;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    let response: Response;
    try {
      response = await fetch(apiUrlFor(path), {
        ...init,
        headers
      });
    } catch (cause) {
      if (isAbortError(cause)) {
        throw cause;
      }

      if (canRetry && attempt < maxAttempts - 1) {
        await wait(retryDelayMs(attempt));
        continue;
      }

      void emitClientErrorTelemetry({
        path,
        method,
        code: "network_error",
        isRetryable: canRetry,
        isEdgeBlocked: false,
        responseText: cause instanceof Error ? cause.message : undefined
      });

      throw new PortalApiError("Network request failed", {
        code: "network_error",
        isRetryable: canRetry,
        responseText: cause instanceof Error ? cause.message : undefined
      });
    }

    if (!response.ok && canRetry && RETRYABLE_STATUS_CODES.has(response.status) && attempt < maxAttempts - 1) {
      await wait(retryDelayMs(attempt));
      continue;
    }

    if (!response.ok) {
      let payload: unknown = null;
      let responseText = "";
      try {
        const contentType = response.headers.get("content-type") || "";
        if (contentType.toLowerCase().includes("application/json")) {
          payload = await response.json();
        } else {
          responseText = await response.text();
        }
      } catch {
        payload = null;
      }

      let validation: unknown;
      let code: string | undefined;
      if (payload && typeof payload === "object" && "validation" in payload) {
        validation = (payload as { validation?: unknown }).validation;
      }
      if (payload && typeof payload === "object" && "error" in payload) {
        const rawCode = (payload as { error?: unknown }).error;
        code = typeof rawCode === "string" ? rawCode : undefined;
      }
      const requestId =
        response.headers.get("x-amzn-requestid") ||
        response.headers.get("x-amz-apigw-id") ||
        response.headers.get("x-request-id");
      const isEdgeBlocked = isCloudFrontBlockedResponse(response.status, responseText);
      const isRetryable = RETRYABLE_STATUS_CODES.has(response.status);

      void emitClientErrorTelemetry({
        path,
        method,
        status: response.status,
        code,
        isEdgeBlocked,
        isRetryable,
        requestId,
        responseText
      });

      throw new PortalApiError(`Request failed with status ${response.status}`, {
        status: response.status,
        payload,
        validation,
        responseText,
        code,
        isEdgeBlocked,
        isRetryable
      });
    }

    if (response.status === 204) {
      return undefined as T;
    }
    return response.json() as Promise<T>;
  }

  throw new PortalApiError("Request failed", { code: "unknown_request_error", isRetryable: canRetry });
}

export async function getApiHealth(): Promise<ApiHealth> {
  if (shouldUseMockApi()) {
    return {
      ok: true,
      environment: appConfig.environment,
      authenticated: true
    };
  }

  return request<ApiHealth>("/api/health");
}

export async function getRuntimePortalConfig(): Promise<RuntimePortalConfig> {
  if (shouldUseMockApi()) {
    return {
      environment: appConfig.environment,
      showTestBanner: appConfig.showTestBanner,
      testBannerText: appConfig.showTestBanner ? "TEST ENVIRONMENT" : null,
      support: {
        email: appConfig.support.email,
        phone: appConfig.support.phone,
        phoneHref: `tel:+${appConfig.support.phoneDigits}`,
        technologyEmail: ""
      },
      maintenance: appConfig.maintenance,
      legal: {
        privacyPolicyPath: "/privacy-policy",
        termsOfServicePath: "/terms-of-service"
      }
    };
  }

  return request<RuntimePortalConfig>("/api/config");
}

const pendingAccountProfiles = new Map<string, Promise<AccountProfile>>();

export async function getAccountProfile(): Promise<AccountProfile> {
  if (shouldUseMockApi()) return structuredClone(mockAccount);
  const accessToken = await getAccessToken();
  // Share only overlapping reads using the same credential, never resolved profiles.
  const key = JSON.stringify([apiUrlFor("/api/me"), accessToken]);
  const pending = pendingAccountProfiles.get(key);
  if (pending) return pending;
  const result = request<AccountProfile>("/api/me", undefined, { accessToken })
    .finally(() => pendingAccountProfiles.delete(key));
  pendingAccountProfiles.set(key, result);
  return result;
}

export async function getPracticeOptions(): Promise<PracticeOption[]> {
  if (shouldUseMockApi()) {
    return mockAccount.practices.map((practice) => ({
      id: practice.id,
      name: practice.name,
      address: practice.address,
      label: `${practice.name} - ${practice.address}`
    }));
  }

  const response = await request<{ options: PracticeOption[] }>("/api/practice-options");
  return response.options;
}

export async function getPublicPracticeOptions(): Promise<PracticeOption[]> {
  if (shouldUseMockApi()) {
    return getPracticeOptions();
  }

  const response = await request<{ options: PracticeOption[] }>("/api/public/practice-options");
  return response.options;
}

export async function searchPracticeAddresses(search: string, addressContext?: "owner"): Promise<PracticeAddress[]> {
  if (shouldUseMockApi()) {
    return [
      {
        id: "mock-address",
        label: `${search}, Raleigh, NC 27601`,
        line1: search,
        city: "Raleigh",
        state: "NC",
        postalCode: "27601"
      }
    ];
  }

  const params = new URLSearchParams({ search });
  if (addressContext) params.set("addressContext", addressContext);
  const response = await request<{ suggestions: PracticeAddress[] }>(`/api/practice-addresses?${params.toString()}`);
  return response.suggestions;
}

export async function updateAccountProfile(update: AccountUpdate): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    Object.assign(mockAccount, update);
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>("/api/me", {
    method: "PATCH",
    body: JSON.stringify(update)
  });
}

export async function submitAccessRequest(input: AccessRequestInput): Promise<AccessRequestResult> {
  if (shouldUseMockApi()) {
    return {
      ok: true,
      status: 201,
      code: "submitted",
      message: "Your request has been submitted. If approved, you will receive an email with a link to complete your registration."
    };
  }

  return request<AccessRequestResult>("/api/access-request", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function requestRegistrationLink(input: { email: string }): Promise<RegistrationLinkRequestResult> {
  if (shouldUseMockApi()) {
    return {
      ok: true,
      queued: true,
      code: "queued",
      message: "Your request was sent to our team. If approved, you will receive a new registration email."
    };
  }

  return request<RegistrationLinkRequestResult>("/api/registration/link-request", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function submitFeedback(input: FeedbackInput): Promise<FeedbackResult> {
  if (shouldUseMockApi()) {
    return {
      ok: true,
      status: "sent",
      message: "Thank you. Your feedback was sent to our team.",
      ticketId: "mock-feedback-ticket",
      ticketNumber: "TEST-1"
    };
  }

  return request<FeedbackResult>("/api/feedback", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function getRegistrationPrefill(): Promise<RegistrationPrefill> {
  if (shouldUseMockApi()) {
    return {
      approved: true,
      profile: {
        firstName: mockAccount.firstName,
        lastName: mockAccount.lastName,
        email: mockAccount.email,
        phone: mockAccount.phone,
        deaNumber: mockAccount.deaNumber,
        licenseNumber: mockAccount.licenseNumber,
        licenseState: mockAccount.licenseState
      },
      practice: {
        id: mockAccount.practices[0]?.id ?? null,
        name: mockAccount.practices[0]?.name ?? ""
      }
    };
  }

  return request<RegistrationPrefill>("/api/registration/prefill");
}

export async function completeRegistration(input: RegistrationCompleteInput): Promise<RegistrationCompleteResult> {
  if (shouldUseMockApi()) {
    Object.assign(mockAccount, {
      firstName: input.firstName,
      lastName: input.lastName,
      phone: input.phone,
      deaNumber: input.deaNumber ?? mockAccount.deaNumber
    });
    if (input.signatureBase64) {
      mockAccount.hasSignature = true;
    }
    return { ok: true, account: structuredClone(mockAccount) };
  }

  return request<RegistrationCompleteResult>("/api/registration/complete", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

export async function addPractice(input: PracticeCreate): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    const name = input.mode === "existing"
      ? getPracticeNameForMock(input.practiceId)
      : input.name;
    const address = input.mode === "existing"
      ? "Existing practice address"
      : input.address.label;
    const practice: Practice = {
      id: `practice-${crypto.randomUUID()}`,
      name,
      address,
      isPrimary: false,
      status: "pending"
    };
    mockAccount.practices.push(practice);
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>("/api/me/practices", {
    method: "POST",
    body: JSON.stringify(input)
  });
}

function getPracticeNameForMock(practiceId: string): string {
  return mockAccount.practices.find((practice) => practice.id === practiceId)?.name ?? "Selected practice";
}

export async function setPrimaryPractice(practiceId: string): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    mockAccount.practices = mockAccount.practices.map((practice) => ({
      ...practice,
      isPrimary: practice.status === "active" && practice.id === practiceId
    }));
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>(`/api/me/practices/${practiceId}/primary`, {
    method: "PUT"
  });
}

export async function removePractice(practiceId: string): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    const practice = mockAccount.practices.find((item) => item.id === practiceId);
    if (practice?.isPrimary) throw new Error("Primary practice cannot be removed");
    mockAccount.practices = mockAccount.practices.filter((practice) => practice.id !== practiceId);
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>(`/api/me/practices/${practiceId}`, {
    method: "DELETE"
  });
}

export async function updateSignature(signatureBase64: string): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    mockAccount.hasSignature = true;
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>("/api/me/signature", {
    method: "PUT",
    body: JSON.stringify({ signatureBase64 })
  });
}

export async function removeSignature(): Promise<AccountProfile> {
  if (shouldUseMockApi()) {
    mockAccount.hasSignature = false;
    return structuredClone(mockAccount);
  }

  return request<AccountProfile>("/api/me/signature", {
    method: "DELETE"
  });
}

export async function deleteAccount(input: AccountDeleteRequest): Promise<void> {
  if (shouldUseMockApi()) return;

  await request<void>("/api/me", {
    method: "DELETE",
    body: JSON.stringify(input)
  });
}

export async function getOrderFormPrefill(
  practiceId: string,
  options: {
    includePatients?: boolean;
    includePortalPatients?: boolean;
    includePharmetikaPatients?: boolean;
    includeProducts?: boolean;
  } = {}
): Promise<OrderFormPrefill> {
  if (shouldUseMockApi()) {
    const selectedPractice = mockAccount.practices.find((practice) => practice.id === practiceId);
    if (!selectedPractice) {
      throw new Error("Practice unavailable");
    }

    return {
      selectedPractice: {
        id: selectedPractice.id,
        name: selectedPractice.name,
        address: selectedPractice.address,
        isPrimary: selectedPractice.isPrimary
      },
      patientOptions: [
        {
          patientId: "demo-patient-max",
          label: "Maximo | Demo | Dog | Male | 55 lb",
          source: "demo",
          sources: ["portal"],
          canUpdateDemographics: true,
          demographics: {
            name: "Maximo",
            firstName: "Maximo",
            lastName: null,
            dob: "2020-01-15",
            sex: "male",
            sexLabel: "Male",
            species: "canine",
            speciesLabel: "Dog",
            weight: "55",
            weightUnit: "lb"
          },
          owner: {
            firstName: "Demo",
            lastName: "Owner",
            phone: "(555) 555-5555",
            email: "demo.owner@example.com",
            address: {
              line1: null,
              line2: null,
              city: null,
              state: null,
              postalCode: null
            }
          }
        },
        {
          patientId: "demo-patient-newest",
          label: "Newest Ownerlast | Demo | Cat | Unknown",
          source: "demo",
          sources: ["pharmetika"],
          canUpdateDemographics: true,
          demographics: {
            name: "Newest Ownerlast",
            firstName: "Newest",
            lastName: "Ownerlast",
            dob: null,
            sex: "unknown",
            sexLabel: "Unknown",
            species: "feline",
            speciesLabel: "Cat",
            weight: "",
            weightUnit: "lb"
          },
          owner: {
            firstName: "Demo",
            lastName: "Ownerlast",
            phone: "",
            email: "",
            address: {
              line1: null,
              line2: null,
              city: null,
              state: null,
              postalCode: null
            }
          }
        }
      ],
      productOptions: [
        {
          productId: "demo-product-acepromazine",
          label: "Acepromazine 10mg Tablet",
          drugName: "Acepromazine",
          strength: "10mg",
          dosageForm: "Tablet",
          defaultQuantity: null,
          quantityUnit: null,
          deaSchedule: null
        },
        {
          productId: "demo-product-methimazole",
          label: "Methimazole 2.5mg Chew",
          drugName: "Methimazole",
          strength: "2.5mg",
          dosageForm: "Chew",
          defaultQuantity: null,
          quantityUnit: null,
          deaSchedule: null
        }
      ],
      speciesOptions: [
        { value: "human", label: "Human", sourceCode: "human", sourceLabel: "human" },
        { value: "canine", label: "Dog", sourceCode: "canine", sourceLabel: "canine" },
        { value: "feline", label: "Cat", sourceCode: "feline", sourceLabel: "feline" },
        { value: "equine", label: "Horse", sourceCode: "equine", sourceLabel: "equine" },
        { value: "avian", label: "Bird", sourceCode: "avian", sourceLabel: "avian" },
        { value: "mammal-large", label: "Mammal - Large", sourceCode: "mammal-large", sourceLabel: "mammal - large" },
        { value: "mammal-other", label: "Other", sourceCode: "mammal-other", sourceLabel: "mammal - other" },
        { value: "mammal-small", label: "Mammal - Small", sourceCode: "mammal-small", sourceLabel: "mammal - small" },
        { value: "reptilian", label: "Reptile", sourceCode: "reptilian", sourceLabel: "reptilian" },
        { value: "swine", label: "Pig", sourceCode: "swine", sourceLabel: "swine" }
      ],
      warnings: []
    };
  }

  const params = new URLSearchParams({ practiceId });
  if (options.includePatients === false) params.set("includePatients", "false");
  if (options.includePortalPatients === false) params.set("includePortalPatients", "false");
  if (options.includePharmetikaPatients === false) params.set("includePharmetikaPatients", "false");
  if (options.includeProducts === false) params.set("includeProducts", "false");
  return request<OrderFormPrefill>(`/api/order-form/prefill?${params.toString()}`);
}

export async function getProductDetail(
  practiceId: string,
  productId: string,
  quantity?: string,
  init?: RequestInit
): Promise<ProductDetail> {
  const params = new URLSearchParams({ practiceId });
  if (quantity) params.set("quantity", quantity);
  return request<ProductDetail>(`/api/order-form/products/${encodeURIComponent(productId)}?${params.toString()}`, init);
}

export async function getPreviousPrescriptions(
  practiceId: string,
  patientId: string
): Promise<PreviousPrescriptionsResult> {
  const params = new URLSearchParams({ practiceId });
  return request<PreviousPrescriptionsResult>(
    `/api/order-form/patients/${encodeURIComponent(patientId)}/previous-prescriptions?${params.toString()}`
  );
}

export async function listOrders(query = "", limit = 50, practiceId = "", offset = 0): Promise<OrderListResult> {
  const params = new URLSearchParams();
  if (query.trim()) params.set("query", query.trim());
  if (practiceId.trim()) params.set("practiceId", practiceId.trim());
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  return request<OrderListResult>(`/api/orders?${params.toString()}`);
}

export async function getOrderDetail(orderId: string): Promise<OrderDetail> {
  return request<OrderDetail>(`/api/orders/${encodeURIComponent(orderId)}`);
}

export async function getOrderPdfStatus(orderId: string): Promise<OrderPdfStatus> {
  return request<OrderPdfStatus>(`/api/orders/${encodeURIComponent(orderId)}/pdf`);
}

export async function requestOrderPdf(orderId: string): Promise<OrderPdfStatus> {
  return request<OrderPdfStatus>(`/api/orders/${encodeURIComponent(orderId)}/pdf`, {
    method: "POST"
  });
}

export async function submitPrescription(input: SubmitPrescriptionInput): Promise<SubmitPrescriptionResult> {
  return request<SubmitPrescriptionResult>("/api/orders", {
    method: "POST",
    body: JSON.stringify(input)
  });
}
